// The import's draft on the server (release 3.3, import redesign I2; plan
// 4.8), as plain functions and one small class the node tests drive with a
// mocked fetch.
//
// # WHAT IS WRITTEN, AND WHEN
//
// Every Next (the step changes) writes at once; every other change is written
// after DRAFT_QUIET_MS of quiet. A write carries the whole state the wizard
// keeps, because the server replaces it: the column choices, the
// confirmations, the columns a person chose by hand (byPerson), the answers to
// the file's questions, "Remember this mapping as", the value answers, the
// duplicate rule, and where the person was on Review and fix. Fixes and holds
// are not in it: they are written on the rows as they are made.
//
// # ONE WRITE AT A TIME, AND A STALE ONE
//
// Writes go one after another, each with the version the last one returned,
// and a write asked for while one is on its way waits and sends only the
// newest state. A write refused as stale (409 DRAFT_STALE: another tab or
// device saved since) is not retried over the other one: the draft is read
// again, the screen takes it, and says so in one quiet line.

import { api, ApiError, type ImportAnalysis, type ImportDraftState, type ImportDraftWrite, type ImportDuplicates, type ImportServerDraft } from '../../lib/api'
import { IMPORT_STEP_NAMES } from '../../lib/importDraft'
import { errorWords } from '../../lib/importWords'
import { markErrorShown } from '../../lib/feedback'
import { initialAnswers, initialChoices, type ValueAnswers } from './shared'
import { defaultMappingName, mappingName } from './columnTiers'

export const DRAFT_QUIET_MS = 1000
export const DRAFT_SAVED = 'Draft saved'
export const DRAFT_RELOADED = 'Changed in another tab; reloaded'

export type Remember = { on: boolean; name: string }

// What the wizard holds that the draft keeps.
export type DraftValues = {
  choices: Record<string, string>
  confirmed: Record<string, boolean>
  byPerson: string[]
  answers: Record<string, string>
  remember: Remember
  valueAnswers: ValueAnswers
  duplicates: ImportDuplicates
  fill: Record<string, string>
  filter: string
  page: number
}

// The write for a step (1 to 5), without its version. A remembered mapping
// with no name typed goes with the name it would get, so the draft is never
// refused for an empty name.
export function draftBody(step: number, v: DraftValues, analysis: ImportAnalysis): Omit<ImportDraftWrite, 'version'> {
  const state: ImportDraftState = {
    columns: { ...v.choices },
    confirmed: Object.keys(v.confirmed)
      .filter((c) => v.confirmed[c])
      .sort(),
    byPerson: [...new Set(v.byPerson)].sort(),
    answers: { ...v.answers },
    values: v.valueAnswers,
    duplicates: v.duplicates,
    fill: v.fill,
    remember: { on: v.remember.on, name: mappingName(v.remember.name) || defaultMappingName(analysis) },
    review: { filter: v.filter === 'all' ? '' : v.filter, page: Math.max(0, v.page) },
  }
  const n = Math.min(Math.max(step, 1), 5)
  return { step: IMPORT_STEP_NAMES[n - 1] ?? 'upload', state }
}

// What a draft read back gives the wizard. Only what still fits the file is
// taken: a choice for a column the file no longer has, or an answer that is no
// longer one of a question's options, is dropped (initialChoices and
// initialAnswers decide that, as they do for a file read again).
export function restoreDraft(analysis: ImportAnalysis, d: Pick<ImportServerDraft, 'state'>): DraftValues {
  const s = d.state ?? ({} as Partial<ImportDraftState>)
  const columns = new Set(analysis.columns.map((c) => c.column))
  const confirmed: Record<string, boolean> = {}
  for (const c of s.confirmed ?? []) if (columns.has(c)) confirmed[c] = true
  const remember = s.remember ? { on: s.remember.on, name: s.remember.name || defaultMappingName(analysis) } : { on: true, name: defaultMappingName(analysis) }
  const dupes = s.duplicates
  return {
    choices: initialChoices(analysis, s.columns ?? {}),
    confirmed,
    byPerson: (s.byPerson ?? []).filter((c) => columns.has(c)),
    answers: initialAnswers(analysis, s.answers ?? {}),
    remember,
    valueAnswers: s.values ?? {},
    duplicates: dupes === 'SKIP' || dupes === 'UPDATE' || dupes === 'FILL_EMPTY' ? dupes : 'FILL_EMPTY',
    fill: s.fill ?? {},
    filter: s.review?.filter || 'all',
    page: s.review?.page ?? 0,
  }
}

// The one line the footer says when a write failed for another reason than a
// stale version: the server's own words.
export function draftFailedWords(err: unknown): string {
  return 'Draft not saved. ' + errorWords(err)
}

export type DraftEvent =
  | { kind: 'saved'; draft: ImportServerDraft }
  | { kind: 'reloaded'; draft: ImportServerDraft }
  | { kind: 'failed'; error: unknown }

export class DraftSync {
  readonly id: string
  version: number
  private readonly on: (e: DraftEvent) => void
  private pending: Omit<ImportDraftWrite, 'version'> | null = null
  private running: Promise<void> | null = null

  constructor(id: string, version: number, on: (e: DraftEvent) => void) {
    this.id = id
    this.version = version
    this.on = on
  }

  // Asks for a write of this state. Returns when every write asked for so far
  // has settled, which only the tests wait for.
  save(body: Omit<ImportDraftWrite, 'version'>): Promise<void> {
    this.pending = body
    if (!this.running) {
      this.running = this.run().finally(() => {
        this.running = null
      })
    }
    return this.running
  }

  private async run(): Promise<void> {
    while (this.pending) {
      const body = this.pending
      this.pending = null
      try {
        const d = await api.putImportDraft(this.id, { ...body, version: this.version })
        this.version = d.version
        this.on({ kind: 'saved', draft: d })
      } catch (err) {
        // Said here, in the footer: the console's net adds no toast of its own.
        markErrorShown(err)
        if (err instanceof ApiError && err.status === 409 && err.code === 'DRAFT_STALE') {
          // The other tab's draft is the newer one: nothing asked for here
          // before it is read again is written over it.
          try {
            const d = await api.importDraft(this.id)
            this.version = d.version
            this.pending = null
            this.on({ kind: 'reloaded', draft: d })
          } catch (again) {
            this.pending = null
            markErrorShown(again)
            this.on({ kind: 'failed', error: again })
          }
        } else {
          this.on({ kind: 'failed', error: err })
        }
      }
    }
  }
}
